import { useState, useCallback } from 'react';
import { Provider } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/components/ui/use-toast";
import { supabase, APP_URL } from '@/lib/supabase';

interface SocialAuthProps {
  mode: 'login' | 'signup';
  className?: string;
  onSuccess?: () => void;
}

export const SocialAuth = ({ mode, className = "", onSuccess }: SocialAuthProps) => {
  const [isLoading, setIsLoading] = useState<Provider | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const openAuthPopup = useCallback(async (url: string): Promise<any> => {
    const width = 600;
    const height = 800;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;

    const popup = window.open(
      url,
      'Auth',
      `width=${width},height=${height},left=${left},top=${top},popup=1`
    );

    if (!popup) {
      throw new Error('Popup blocked. Please enable popups for this site.');
    }

    return new Promise((resolve, reject) => {
      let timeout: NodeJS.Timeout;

      const checkAuth = async () => {
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) throw error;
          
          if (session?.user) {
            if (popup) popup.close();
            resolve(session);
            return true;
          }
          return false;
        } catch (error) {
          return false;
        }
      };

      const intervalId = setInterval(async () => {
        const authenticated = await checkAuth();
        if (authenticated) {
          clearInterval(intervalId);
          clearTimeout(timeout);
        }
      }, 1000);

      // Set a timeout to stop checking after 5 minutes
      timeout = setTimeout(() => {
        clearInterval(intervalId);
        if (popup) popup.close();
        reject(new Error('Authentication timeout'));
      }, 300000); // 5 minutes

      // Check if popup is closed
      const closedCheckerId = setInterval(() => {
        if (popup?.closed) {
          clearInterval(intervalId);
          clearInterval(closedCheckerId);
          clearTimeout(timeout);
          reject(new Error('Authentication cancelled'));
        }
      }, 500);
    });
  }, []);

  const handleSocialAuth = async (provider: Provider) => {
    try {
      setIsLoading(provider);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          skipBrowserRedirect: true,
          redirectTo: `${APP_URL}/auth/callback`,
          queryParams: {
            ...(provider === 'google' && {
              access_type: 'offline',
              prompt: 'select_account',
            }),
            ...(provider === 'facebook' && {
              auth_type: 'reauthenticate',
              display: 'popup',
            }),
          },
        },
      });

      if (error) throw error;
      if (!data?.url) throw new Error('No authentication URL returned');

      const session = await openAuthPopup(data.url);
      
      if (session) {
        toast({
          title: "Success",
          description: `Successfully ${mode === 'login' ? 'logged in' : 'signed up'}!`,
        });
        onSuccess?.();
        navigate('/albums');
      }

    } catch (error: any) {
      console.error("Social auth error:", error);
      if (error.message !== 'Authentication cancelled') {
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: error.message || "Failed to authenticate. Please try again.",
        });
      }
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <button
        onClick={() => handleSocialAuth("google")}
        disabled={isLoading !== null}
        className="w-full h-10 px-4 flex items-center justify-center gap-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#EA4335"
            d="M5.266 9.765A7.077 7.077 0 0 1 12 4.909c1.69 0 3.218.6 4.418 1.582L19.91 3C17.782 1.145 15.055 0 12 0 7.27 0 3.198 2.698 1.24 6.65l4.026 3.115Z"
          />
          <path
            fill="#34A853"
            d="M16.04 18.013c-1.09.703-2.474 1.078-4.04 1.078a7.077 7.077 0 0 1-6.723-4.823l-4.04 3.067A11.965 11.965 0 0 0 12 24c2.933 0 5.735-1.043 7.834-3l-3.793-2.987Z"
          />
          <path
            fill="#4A90E2"
            d="M19.834 21c2.195-2.048 3.62-5.096 3.62-9 0-.71-.109-1.473-.272-2.182H12v4.637h6.436c-.317 1.559-1.17 2.766-2.395 3.558L19.834 21Z"
          />
          <path
            fill="#FBBC05"
            d="M5.277 14.268A7.12 7.12 0 0 1 4.909 12c0-.782.125-1.533.357-2.235L1.24 6.65A11.934 11.934 0 0 0 0 12c0 1.92.445 3.73 1.237 5.335l4.04-3.067Z"
          />
        </svg>
        {isLoading === "google" ? (
          <div className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
        ) : (
          `Continue with Google`
        )}
      </button>

      {/* <button
        onClick={() => handleSocialAuth("facebook")}
        disabled={isLoading !== null}
        className="w-full h-10 px-4 flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#0C63D4] border border-transparent rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1877F2] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9.945 22v-8.834H7V9.485h2.945V6.54c0-3.043 1.926-4.54 4.64-4.54 1.3 0 2.418.097 2.744.14v3.18h-1.883c-1.476 0-1.82.703-1.82 1.732v2.433h3.68l-.736 3.68h-2.944L13.685 22" />
        </svg>
        {isLoading === "facebook" ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          `Continue with Facebook`
        )}
      </button> */}
    </div>
  );
};

export default SocialAuth;